import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Mail, LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

/**
 * Full-screen gate shown to authenticated users whose email is not yet verified.
 * Allowed exits: Resend verification email, Sign Out, Go to Profile (to fix typo'd email).
 */
export default function EmailVerificationGate() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [devUrl, setDevUrl] = useState(null);
  const [justSent, setJustSent] = useState(false);

  const resend = async () => {
    setSending(true);
    try {
      const res = await api.post('/auth/resend-verification');
      if (res.data?.email_sent) {
        toast.success('Verification email sent. Please check your inbox.');
      } else {
        toast.success('Verification link generated.');
      }
      if (res.data?.dev_verify_url) setDevUrl(res.data.dev_verify_url);
      setJustSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send verification email');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12"
      data-testid="email-verification-gate"
    >
      <div className="max-w-xl w-full text-center animate-fade-up">
        {/* Mystical mail icon */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div className="absolute inset-0 rounded-full bg-gold/15 animate-breathe" />
          <div className="absolute inset-3 rounded-full bg-gold/10 animate-breathe" style={{ animationDelay: '0.5s' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Mail size={32} className="text-gold" />
          </div>
        </div>

        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-4">Almost in</p>
        <h1 className="font-serif text-3xl sm:text-4xl font-light text-dark tracking-tight mystical-heading mb-4">
          Please verify your email
        </h1>
        <p className="text-text-muted font-sans text-base leading-relaxed max-w-md mx-auto mb-2">
          We sent a verification link to
        </p>
        <p className="font-serif text-lg text-dark mb-6 break-all" data-testid="gate-user-email">
          {user?.email}
        </p>
        <p className="text-text-muted font-sans text-sm leading-relaxed max-w-md mx-auto mb-10">
          Click the link in your inbox to unlock your full sacred space. If you don't see it, check your spam folder —
          or tap below to send another.
        </p>

        {devUrl && (
          <div className="mb-6 p-4 rounded-xl bg-bone/60 border border-gold/20 text-left">
            <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-gold mb-2">Dev mode link</p>
            <a href={devUrl} className="font-sans text-xs text-dark underline break-all" data-testid="gate-dev-url">
              {devUrl}
            </a>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            onClick={resend}
            disabled={sending}
            data-testid="gate-resend-button"
            className="h-12 px-8 bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-xs tracking-[0.25em] uppercase btn-hover"
          >
            <Mail size={14} className="mr-2" />
            {sending ? 'Sending...' : justSent ? 'Resend Again' : 'Resend Email'}
          </Button>
          <Button
            onClick={() => navigate('/profile')}
            variant="outline"
            data-testid="gate-profile-button"
            className="h-12 px-6 border-gold/20 bg-bone/40 hover:bg-bone text-dark rounded-xl font-sans text-xs tracking-[0.25em] uppercase"
          >
            <Settings size={14} className="mr-2" /> Change Email
          </Button>
        </div>

        <button
          onClick={handleLogout}
          data-testid="gate-logout-button"
          className="mt-8 text-text-muted hover:text-dark font-sans text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
          style={{ transition: 'color 0.2s ease' }}
        >
          <LogOut size={12} /> Sign Out
        </button>
      </div>
    </div>
  );
}
